
import { useState, useMemo, useRef, useEffect } from 'react';
import { Search, MapPin, Activity, Check, AlertCircle, LayoutGrid } from 'lucide-react';

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  type = 'doctor', // 'doctor' or 'hospital'
  disabled = false,
  onBrowseDirectory // Optional callback to open a larger modal
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDoctorName = (opt) => {
    if (!opt) return 'Specialist';
    let name = '';
    if (opt.user) {
      if (opt.user.firstName || opt.user.lastName) {
        name = `${opt.user.firstName || ''} ${opt.user.lastName || ''}`.trim();
      } else if (opt.user.name) {
        name = opt.user.name;
      }
    }
    if (!name && opt.name) {
      name = opt.name;
    }
    if (!name) return 'Specialist';

    // Prevent "Dr. Dr."
    const lowerName = name.toLowerCase();
    if (lowerName.startsWith('dr.') || lowerName.startsWith('dr ')) {
      // Ensure capitalized "Dr. "
      return `Dr. ${name.substring(name.indexOf(' ') + 1)}`;
    }
    return `Dr. ${name}`;
  };

  const selectedOption = useMemo(() => {
    return options.find(opt => opt._id === value);
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    const s = search.toLowerCase();
    return options.filter(opt => {
      // Name Match (Doctor Full Name or Hospital Name)
      const fullName = type === 'doctor'
        ? getDoctorName(opt).toLowerCase()
        : (opt.hospitalName || '').toLowerCase();
      const matchName = fullName.includes(s);

      // Location Match
      const matchLoc = (opt.location || opt.city || opt.address?.city || '').toLowerCase().includes(s);

      // Specialization Match
      const matchSpec = (opt.specialization || '').toLowerCase().includes(s);

      // Contact Match (Email or Phone)
      const contactInfo = type === 'doctor'
        ? `${opt.user?.email || ''} ${opt.user?.phone || ''}`.toLowerCase()
        : `${opt.contactPerson?.email || ''} ${opt.contactPerson?.phone || ''}`.toLowerCase();
      const matchContact = contactInfo.includes(s);

      return matchName || matchLoc || matchSpec || matchContact;
    });
  }, [options, search, type]);

  // Sync search input with selected value on open for better visibility
  useEffect(() => {
    if (isOpen && selectedOption) {
      const name = type === 'doctor'
        ? getDoctorName(selectedOption)
        : (selectedOption.hospitalName || '');
      setSearch(name);
    }
  }, [isOpen, selectedOption, type]);

  return (
    <div className="relative space-y-2" ref={dropdownRef}>
      <label className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1">{label}</label>

      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-2 rounded-xl transition-all ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-100' :
          isOpen ? 'border-[#143E78] shadow-lg ring-2 ring-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
      >
        {selectedOption ? (
          <div className="flex flex-col items-start overflow-hidden">
            <span className="font-black text-slate-900 truncate text-[11px] sm:text-sm">
              {type === 'doctor'
                ? getDoctorName(selectedOption)
                : selectedOption.hospitalName}
            </span>
            <span className="text-[8px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-wider truncate">
              {type === 'doctor'
                ? (selectedOption.specialization || 'Consultant Surgeon')
                : (selectedOption.address?.city || 'Primary Center')}
            </span>
          </div>
        ) : (
          <span className="text-slate-300 font-black text-[9px] sm:text-xs uppercase tracking-widest">{placeholder}</span>
        )}
        <div className="flex items-center gap-2">
          {onBrowseDirectory && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onBrowseDirectory();
              }}
              className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-[#143E78] transition-all"
              title="Browse Directory"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          )}
          {!disabled && <Search className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isOpen ? 'text-[#143E78]' : 'text-slate-300'}`} />}
        </div>
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-[60] w-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden animate-slideDown">
          <div className="p-2 sm:p-3 border-b border-slate-100 bg-slate-50/50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400" />
              <input
                autoFocus
                type="text"
                placeholder={`Search ${type === 'doctor' ? 'doctor' : 'hospital'}...`}
                className="w-full pl-8 sm:pl-9 pr-3 sm:pr-4 py-1.5 sm:py-2 bg-white border border-slate-200 rounded-lg text-[10px] sm:text-xs outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="max-h-[250px] sm:max-h-[280px] overflow-y-auto scrollbar-thin relative">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <button
                  key={opt._id}
                  type="button"
                  onClick={() => {
                    onChange(opt._id);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-blue-50/50 transition-colors border-b border-slate-50 last:border-0 ${value === opt._id ? 'bg-blue-50' : ''
                    }`}
                  title={type === 'doctor'
                    ? `Phone: ${opt.user?.phone || 'N/A'}\nEmail: ${opt.user?.email || 'N/A'}\nHospital: ${opt.workingHospital || 'N/A'}`
                    : `Phone: ${opt.contactPerson?.phone || 'N/A'}\nEmail: ${opt.contactPerson?.email || 'N/A'}`
                  }
                >
                  <div className="flex gap-2 sm:gap-3 items-center flex-1 overflow-hidden">
                    <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${type === 'doctor' ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-600'}`}>
                      {type === 'doctor' ? <Activity className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                    </div>
                    <div className="text-left overflow-hidden">
                      <p className="text-xs sm:text-sm font-bold text-slate-900 leading-tight truncate">
                        {type === 'doctor'
                          ? getDoctorName(opt)
                          : opt.hospitalName}
                      </p>
                      <p className="text-[8px] sm:text-[10px] font-semibold text-slate-500 uppercase tracking-tighter truncate">
                        {type === 'doctor'
                          ? `${opt.specialization || 'Surgeon'} • ${opt.location || opt.city || opt.address?.city || 'Center'}`
                          : `${opt.address?.city || 'Local Area'} • ${opt.availableBeds || 'TBD'} Beds`}
                      </p>
                      {opt.user?.email && (
                        <p className="text-[9px] font-medium text-slate-400 mt-0.5 lowercase italic">
                          {opt.user.email}
                        </p>
                      )}
                      {opt.verifiedBy && (
                        <p className="text-[7px] sm:text-[9px] font-black text-indigo-500 mt-0.5">
                          CC: {opt.verifiedBy.firstName} {opt.verifiedBy.lastName}
                        </p>
                      )}
                    </div>
                  </div>
                  {opt.isBusy ? (
                    <span className="flex items-center gap-1 text-[7px] sm:text-[9px] font-black text-orange-500 bg-orange-100 px-1.5 sm:px-2 py-0.5 rounded uppercase flex-shrink-0"><AlertCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> Busy</span>
                  ) : (
                    <div className="flex flex-col items-end gap-1">
                      <span className="flex items-center gap-1 text-[9px] font-black text-emerald-500 bg-emerald-100 px-2 py-0.5 rounded uppercase"><Check className="w-3 h-3" /> Avail</span>
                      {(opt.user?.phone || opt.contactPerson?.phone) && (
                        <div className="text-[10px] text-slate-600 font-black tracking-tighter">
                          {type === 'doctor' ? opt.user?.phone : opt.contactPerson?.phone}
                        </div>
                      )}
                    </div>
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-slate-400 text-xs font-semibold">
                No matching results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
